import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { auth } from '@/lib/api';
import { Loader2, MailCheck } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await auth.forgotPassword(email);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <BrandLogo variant="black" size="lg" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md border-slate-200 rounded-xl shadow-sm">
          {submitted ? (
            <>
              <CardHeader className="text-left">
                <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
                  <MailCheck className="h-6 w-6 text-emerald-600" />
                  Check your email
                </CardTitle>
                <CardDescription className="text-slate-600">
                  If an account exists for that email, we've sent reset instructions.
                </CardDescription>
              </CardHeader>
              <CardContent data-testid="forgot-password-confirmation">
                <Link
                  to="/login"
                  className="font-medium text-emerald-600 hover:text-emerald-700"
                  data-testid="forgot-password-back-to-login-link"
                >
                  Back to login
                </Link>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-left">
                <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
                  Forgot password?
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Enter your email and we'll send you a link to reset it
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="forgot-password-email-input"
                      className="rounded-md"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 rounded-md"
                    disabled={isLoading}
                    data-testid="forgot-password-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </Button>
                </form>

                <p className="mt-6 text-center text-sm text-slate-600">
                  Remembered your password?{' '}
                  <Link
                    to="/login"
                    className="font-medium text-emerald-600 hover:text-emerald-700"
                    data-testid="forgot-password-login-link"
                  >
                    Log in
                  </Link>
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
